/**
 * Responsive Layout Utilities
 * Helper functions for consistent responsive design
 */

import { useTheme, useMediaQuery } from "@mui/material";
import { PADDING, GRID, LAYOUT } from "./constants";

/**
 * Custom hook for responsive breakpoint detection
 */
export const useResponsive = () => {
  const theme = useTheme();

  return {
    isMobile: useMediaQuery(theme.breakpoints.down("sm")),
    isTablet: useMediaQuery(theme.breakpoints.between("sm", "md")),
    isDesktop: useMediaQuery(theme.breakpoints.up("md")),
    isLarge: useMediaQuery(theme.breakpoints.up("lg")),
    isXLarge: useMediaQuery(theme.breakpoints.up("xl")),
  };
};

/**
 * Get responsive spacing based on breakpoint
 */
export const getResponsiveSpacing = (config) => {
  return {
    xs: config.xs || 2,
    sm: config.sm || 3,
    md: config.md || 4,
    lg: config.lg || 6,
  };
};

/**
 * Get responsive padding for pages
 */
export const getPagePadding = () => PADDING.page;

/**
 * Get responsive padding for cards
 */
export const getCardPadding = () => PADDING.card;

/**
 * Get responsive padding for sections
 */
export const getSectionPadding = () => PADDING.section;

/**
 * Get responsive container padding
 */
export const getContainerPadding = () => PADDING.container;

/**
 * Get responsive grid spacing
 */
export const getGridSpacing = () => GRID.containerSpacing;

/**
 * Get responsive font size
 */
export const getResponsiveFontSize = (sizes) => ({
  xs: sizes.xs || "0.875rem",
  sm: sizes.sm || "1rem",
  md: sizes.md || "1.125rem",
  lg: sizes.lg || "1.25rem",
});

/**
 * Get responsive column configuration for grids
 */
export const getResponsiveColumns = (columns = {}) => ({
  xs: columns.xs || 12,
  sm: columns.sm || 6,
  md: columns.md || 4,
  lg: columns.lg || 3,
  xl: columns.xl || 2,
});

/**
 * Generate responsive sx props for consistent spacing
 */
export const responsiveSpacing = (value) => {
  if (typeof value === "number") {
    return value;
  }
  return { xs: value.xs || 2, sm: value.sm || 3, md: value.md || 4 };
};

/**
 * Common responsive container styles (compact)
 */
export const containerStyles = {
  maxWidth: LAYOUT.maxContentWidth,
  mx: "auto",
  px: getPagePadding(),
  width: "100%",
};

/**
 * Common responsive card styles (compact - no extra padding, handled by CardContent)
 */
export const cardStyles = {
  height: "100%",
  display: "flex",
  flexDirection: "column",
  width: "100%",
};

/**
 * Common responsive section styles (compact)
 */
export const sectionStyles = {
  py: getSectionPadding(),
  px: getPagePadding(),
};

/**
 * Responsive button sizes (compact)
 */
export const buttonSizes = {
  small: {
    height: { xs: 28, md: 28 },
    px: { xs: 1.5, md: 1.5 },
    fontSize: { xs: "0.75rem", md: "0.75rem" },
  },
  medium: {
    height: { xs: 32, md: 32 },
    px: { xs: 2, md: 2 },
    fontSize: { xs: "0.8125rem", md: "0.8125rem" },
  },
  large: {
    height: { xs: 36, md: 38 },
    px: { xs: 2.5, md: 3 },
    fontSize: { xs: "0.8125rem", md: "0.875rem" },
  },
};

/**
 * Responsive table configuration (compact)
 */
export const tableResponsive = {
  hideColumnsOnMobile: ["actions", "details"],
  stackOnMobile: true,
  rowHeight: { xs: 36, md: 40 },
};

/**
 * Responsive dialog sizes (compact)
 */
export const dialogSizes = {
  small: { maxWidth: "xs", fullScreen: false },
  medium: { maxWidth: "sm", fullScreen: false },
  large: { maxWidth: "md", fullScreen: { xs: true, sm: false } },
  xlarge: { maxWidth: "lg", fullScreen: { xs: true, sm: false } },
};

/**
 * Helper to make dialogs responsive
 */
export const getResponsiveDialogProps = (size = "medium") => {
  const config = dialogSizes[size];
  return {
    maxWidth: config.maxWidth,
    fullScreen: config.fullScreen,
    fullWidth: true,
  };
};

/**
 * Common flex center styles
 */
export const flexCenter = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

/**
 * Common flex between styles
 */
export const flexBetween = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

/**
 * Common flex column styles
 */
export const flexColumn = {
  display: "flex",
  flexDirection: "column",
};

/**
 * Responsive text alignment
 */
export const responsiveTextAlign = {
  center: { xs: "center", md: "left" },
  left: "left",
  right: "right",
};

/**
 * Hide on mobile
 */
export const hideOnMobile = {
  display: { xs: "none", sm: "block" },
};

/**
 * Hide on desktop
 */
export const hideOnDesktop = {
  display: { xs: "block", sm: "none" },
};

/**
 * Responsive image styles
 */
export const responsiveImage = {
  width: "100%",
  height: "auto",
  display: "block",
  objectFit: "cover",
};

/**
 * Truncate text with ellipsis
 */
export const truncateText = (lines = 1) => ({
  overflow: "hidden",
  textOverflow: "ellipsis",
  display: "-webkit-box",
  WebkitLineClamp: lines,
  WebkitBoxOrient: "vertical",
  wordBreak: "break-word",
});

/**
 * Responsive stack spacing
 */
export const getStackSpacing = (size = "md") => ({
  xs: size === "sm" ? 1 : size === "md" ? 2 : 3,
  md: size === "sm" ? 1.5 : size === "md" ? 2.5 : 4,
});

const ResponsiveUtils = {
  useResponsive,
  getResponsiveSpacing,
  getPagePadding,
  getCardPadding,
  getSectionPadding,
  getContainerPadding,
  getGridSpacing,
  getResponsiveFontSize,
  getResponsiveColumns,
  responsiveSpacing,
  containerStyles,
  cardStyles,
  sectionStyles,
  buttonSizes,
  tableResponsive,
  dialogSizes,
  getResponsiveDialogProps,
  flexCenter,
  flexBetween,
  flexColumn,
  responsiveTextAlign,
  hideOnMobile,
  hideOnDesktop,
  responsiveImage,
  truncateText,
  getStackSpacing,
};

export default ResponsiveUtils;
